import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle, Phone, Smartphone } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { STRUCTURES } from "@/data/content";
import { useSiteSettings } from "@/lib/settings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-sand bg-ivory px-4 py-3 text-sm text-ink placeholder:text-taupe/70 outline-none transition-all duration-300 focus:border-sage focus:ring-2 focus:ring-sage/20";

const Contatti = () => {
  const contacts = useSiteSettings();
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [visit, setVisit] = useState({ name: "", phone: "", email: "", structure: "Da definire", preferred_date: "", preferred_time: "", notes: "" });
  const [sending, setSending] = useState(false);

  const submit = async (path, body, successMsg, reset) => {
    setSending(true);
    try {
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      toast.success(successMsg);
      reset();
    } catch {
      toast.error("Qualcosa è andato storto. Riprova o chiamaci direttamente.");
    } finally {
      setSending(false);
    }
  };

  const infoRows = [
    { icon: Phone, label: "Telefono", value: contacts.phoneDisplay, href: contacts.phoneHref, testid: "contact-phone" },
    { icon: Smartphone, label: "Cellulare", value: contacts.mobileDisplay, href: contacts.mobileHref, testid: "contact-mobile" },
    { icon: MessageCircle, label: "WhatsApp", value: contacts.mobileDisplay, href: contacts.whatsapp, testid: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: contacts.email, href: `mailto:${contacts.email}`, testid: "contact-email" },
    { icon: MapPin, label: "Indirizzo", value: contacts.address, href: null, testid: "contact-address" },
  ];

  return (
    <section id="contatti" data-testid="contatti-section" className="py-24 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          chapter="08"
          eyebrow="Contatti"
          title="Venite a conoscerci di persona"
          text="La scelta di una residenza per una persona cara merita tempo e attenzione. Prenotate una visita guidata o scriveteci: vi risponderemo con cura."
          testid="contatti-heading"
        />

        <div className="mt-16 grid gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="space-y-5" data-testid="contact-info">
              {infoRows.map(({ icon: Icon, label, value, href, testid }) => (
                <div key={label} className="flex items-center gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sage-light text-sage">
                    <Icon size={18} strokeWidth={1.7} />
                  </span>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-taupe">{label}</p>
                    {href ? (
                      <a data-testid={testid} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm font-semibold text-ink transition-colors hover:text-terracotta">
                        {value}
                      </a>
                    ) : (
                      <p data-testid={testid} className="text-sm font-semibold text-ink">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 overflow-hidden rounded-3xl border border-sand shadow-sm" data-testid="contact-map">
              <iframe
                title="Mappa — Orto della Longevità, Castel Giorgio (TR)"
                src={contacts.mapsEmbed}
                className="h-72 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-[2rem] border border-sand bg-card p-7 sm:p-9" data-testid="contact-forms-card">
              <Tabs defaultValue="visita">
                <TabsList className="grid w-full grid-cols-2 rounded-full bg-sand/60 p-1" data-testid="contact-tabs">
                  <TabsTrigger value="visita" className="rounded-full text-[13px] font-semibold data-[state=active]:bg-sage data-[state=active]:text-ivory" data-testid="tab-visit">
                    Prenota una visita
                  </TabsTrigger>
                  <TabsTrigger value="info" className="rounded-full text-[13px] font-semibold data-[state=active]:bg-sage data-[state=active]:text-ivory" data-testid="tab-info">
                    Richiedi informazioni
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="visita" className="mt-7">
                  <form
                    data-testid="visit-form"
                    className="space-y-4"
                    onSubmit={(e) => {
                      e.preventDefault();
                      submit("/visits", visit, "Richiesta di visita inviata. Vi ricontatteremo presto per confermare l'appuntamento.", () =>
                        setVisit({ name: "", phone: "", email: "", structure: "Da definire", preferred_date: "", preferred_time: "", notes: "" })
                      );
                    }}
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <input required data-testid="visit-name-input" className={inputCls} placeholder="Nome e cognome *" value={visit.name} onChange={(e) => setVisit({ ...visit, name: e.target.value })} />
                      <input required data-testid="visit-phone-input" className={inputCls} placeholder="Telefono *" value={visit.phone} onChange={(e) => setVisit({ ...visit, phone: e.target.value })} />
                    </div>
                    <input required type="email" data-testid="visit-email-input" className={inputCls} placeholder="Email *" value={visit.email} onChange={(e) => setVisit({ ...visit, email: e.target.value })} />
                    <div className="grid gap-4 sm:grid-cols-2">
                      <select data-testid="visit-structure-select" className={inputCls} value={visit.structure} onChange={(e) => setVisit({ ...visit, structure: e.target.value })}>
                        <option>Da definire</option>
                        {STRUCTURES.map((s) => (
                          <option key={s.id}>{s.name}</option>
                        ))}
                      </select>
                      <input type="date" data-testid="visit-date-input" className={inputCls} value={visit.preferred_date} onChange={(e) => setVisit({ ...visit, preferred_date: e.target.value })} />
                    </div>
                    <select data-testid="visit-time-select" className={inputCls} value={visit.preferred_time} onChange={(e) => setVisit({ ...visit, preferred_time: e.target.value })}>
                      <option value="">Fascia oraria preferita</option>
                      <option>Mattina</option>
                      <option>Pomeriggio</option>
                    </select>
                    <textarea data-testid="visit-notes-input" rows={3} className={inputCls} placeholder="Note o esigenze particolari" value={visit.notes} onChange={(e) => setVisit({ ...visit, notes: e.target.value })} />
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="visit-submit-button"
                      className="w-full rounded-full bg-sage py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-sage-dark hover:shadow-lg hover:shadow-sage/25 disabled:opacity-60"
                    >
                      {sending ? "Invio in corso…" : "Prenota la tua visita"}
                    </button>
                  </form>
                </TabsContent>

                <TabsContent value="info" className="mt-7">
                  <form
                    data-testid="contact-form"
                    className="space-y-4"
                    onSubmit={(e) => {
                      e.preventDefault();
                      submit("/contact", contact, "Messaggio inviato. Vi risponderemo al più presto.", () => setContact({ name: "", email: "", phone: "", message: "" }));
                    }}
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <input required data-testid="contact-name-input" className={inputCls} placeholder="Nome e cognome *" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                      <input data-testid="contact-phone-input" className={inputCls} placeholder="Telefono" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                    </div>
                    <input required type="email" data-testid="contact-email-input" className={inputCls} placeholder="Email *" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                    <textarea required data-testid="contact-message-input" rows={4} className={inputCls} placeholder="Come possiamo aiutarvi? *" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-submit-button"
                      className="w-full rounded-full bg-terracotta py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-wood hover:shadow-lg hover:shadow-terracotta/25 disabled:opacity-60"
                    >
                      {sending ? "Invio in corso…" : "Invia la richiesta"}
                    </button>
                  </form>
                </TabsContent>
              </Tabs>
              <p className="mt-5 text-center text-xs text-moss">Vi ricontatteremo personalmente. Nessun dato verrà condiviso con terzi.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contatti;
